/**
 * @fileoverview Page to view a document without download capability
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { documentService } from '@/services/documents/documentService';
import { documentAccessService } from '@/services/documents/documentAccessService';
import { formatDate } from '@/utils/formatters';

export const DocumentViewerPage = () => {
  const { documentId } = useParams();
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [accesses, setAccesses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [newAccessEmail, setNewAccessEmail] = useState('');
  const [newAccessName, setNewAccessName] = useState('');
  const [newAccessExpires, setNewAccessExpires] = useState('');
  const [newAccessPermission, setNewAccessPermission] = useState('read');

  useEffect(() => {
    if (!user || !documentId) {
      navigate('/login');
      return;
    }
    loadDocument();
  }, [documentId, user]);

  const loadDocument = () => {
    if (!documentId || !user?.email) return;

    setIsLoading(true);
    try {
      const doc = documentService.getDocumentById(documentId);
      if (!doc) {
        alert(t('docviewer.not_found', 'Documento no encontrado'));
        navigate('/my-documents');
        return;
      }

      // Check if user has access
      const isRecipient = doc.recipientEmail.toLowerCase() === user.email.toLowerCase();
      const hasAccess = documentAccessService.hasAccess(documentId, user.email);

      if (!isRecipient && !hasAccess) {
        alert(t('docviewer.no_access', 'No tienes acceso a este documento'));
        navigate('/my-documents');
        return;
      }

      setDocument(doc);

      // Load accesses if user is the recipient
      if (isRecipient) {
        const docAccesses = documentAccessService.getDocumentAccesses(documentId);
        setAccesses(docAccesses);
      }

    } catch (error) {
      console.error('Error loading document:', error);
      alert(t('docviewer.load_error', 'Error al cargar el documento'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGrantAccess = () => {
    if (!document || !newAccessEmail || !newAccessEmail.includes('@')) {
      alert(t('docviewer.invalid_email', 'Por favor, ingrese un email válido'));
      return;
    }

    try {
      const expiresAt = newAccessExpires ? new Date(newAccessExpires) : undefined;

      documentAccessService.grantAccess({
        documentId: document.id,
        email: newAccessEmail,
        name: newAccessName || undefined,
        permission: newAccessPermission,
        expiresAt,
      }, user?.username || user?.email || 'unknown');

      alert(t('docviewer.access_granted', 'Acceso otorgado correctamente'));
      setShowAccessModal(false);
      setNewAccessEmail('');
      setNewAccessName('');
      setNewAccessExpires('');
      loadDocument();
    } catch (error) {
      console.error('Error granting access:', error);
      alert(t('docviewer.grant_error', 'Error al otorgar acceso'));
    }
  };

  const handleRevokeAccess = (accessId) => {
    if (!confirm(t('docviewer.confirm_revoke', '¿Está seguro de revocar este acceso?'))) return;

    try {
      documentAccessService.revokeAccess(accessId);
      alert(t('docviewer.access_revoked', 'Acceso revocado correctamente'));
      loadDocument();
    } catch (error) {
      console.error('Error revoking access:', error);
      alert(t('docviewer.revoke_error', 'Error al revocar acceso'));
    }
  };

  const isRecipient = document?.recipientEmail.toLowerCase() === user?.email?.toLowerCase();

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="loading-spinner">
          <i className="fas fa-spinner fa-spin"></i>
          <p>{t('common.loading', 'Cargando documento...')}</p>
        </div>
      </div>
    );
  }

  if (!document) {
    return null;
  }

  return (
    <div className="flex-1 w-full p-4 md:p-6 lg:p-10 bg-slate-50/50">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <button
            className="group flex items-center gap-2 text-slate-500 hover:text-blue-600 transition-colors mb-2"
            onClick={() => navigate('/my-documents')}
          >
            <i className="fas fa-arrow-left text-sm group-hover:-translate-x-1 transition-transform"></i>
            <span className="font-medium">{t('docviewer.back_to_certs', 'Volver a mis certificados')}</span>
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">{document.templateName || t('docviewer.certified_doc', 'Documento Certificado')}</h1>
          <p className="text-slate-500 mt-1 flex items-center gap-2">
            <i className="fas fa-shield-alt text-blue-500"></i>
            {t('docviewer.secure_view', 'Visualización segura y verificada')}
          </p>
        </div>

        {isRecipient && (
          <button
            onClick={() => setShowAccessModal(true)}
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold shadow-lg shadow-blue-500/20 active:scale-95 transition-all flex items-center gap-2"
          >
            <i className="fas fa-share-alt"></i>
            <span>{t('common.share', 'Compartir')}</span>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Info & Access */}
        <div className="space-y-8">
          {/* Document Info Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-100/50 flex items-center justify-center text-blue-600">
                <i className="fas fa-info-circle"></i>
              </div>
              <h3 className="font-bold text-slate-800">{t('docviewer.details', 'Detalles')}</h3>
            </div>

            <div className="p-6 space-y-6">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{t('docviewer.issued_by', 'Emitido por')}</label>
                <div className="flex items-center gap-3 text-slate-700">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                    <i className="fas fa-university text-slate-500 text-sm"></i>
                  </div>
                  <span className="font-medium">{document.issuerName}</span>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{t('cert_form.issue_date', 'Fecha de Emisión')}</label>
                <div className="flex items-center gap-3 text-slate-700">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                    <i className="far fa-calendar-alt text-slate-500 text-sm"></i>
                  </div>
                  <span className="font-medium">{formatDate(document.issuedAt)}</span>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{t('history.status', 'Estado')}</label>
                <div>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold
                    ${document.status === 'issued' ? 'bg-green-100 text-green-700' :
                      document.status === 'verified' ? 'bg-blue-100 text-blue-700' :
                        document.status === 'revoked' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}
                  >
                    <i className={`fas fa-${document.status === 'issued' ? 'check-circle' : document.status === 'revoked' ? 'times-circle' : 'clock'}`}></i>
                    {document.status === 'issued' ? t('history.status_issued', 'Emitido') :
                      document.status === 'verified' ? t('history.status_verified', 'Verificado') :
                        document.status === 'revoked' ? t('history.status_revoked', 'Revocado') : t('history.status_pending', 'Pendiente')}
                  </span>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Blockchain Hash</label>
                <div className="group relative">
                  <code className="block w-full p-2.5 bg-slate-50 rounded-lg border border-slate-200 text-xs text-slate-500 font-mono break-all hover:border-blue-200 transition-colors">
                    {document.blockchainHash}
                  </code>
                </div>
              </div>
            </div>
          </div>

          {/* Access Control Card - Recipient Only */}
          {isRecipient && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-100/50 flex items-center justify-center text-indigo-600">
                    <i className="fas fa-users"></i>
                  </div>
                  <h3 className="font-bold text-slate-800">{t('docviewer.accesses', 'Accesos')}</h3>
                </div>
                <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-bold">
                  {accesses.length}
                </span>
              </div>

              <div className="p-0">
                {accesses.length === 0 ? (
                  <div className="p-8 text-center bg-slate-50/30">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3 text-slate-400">
                      <i className="fas fa-lock-open"></i>
                    </div>
                    <p className="text-slate-500 text-sm mb-4">{t('docviewer.private_doc', 'Este documento es privado')}</p>
                    <button
                      onClick={() => setShowAccessModal(true)}
                      className="text-blue-600 text-sm font-semibold hover:underline"
                    >
                      {t('docviewer.grant_first', 'Otorgar primer acceso')}
                    </button>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {accesses.map((access) => (
                      <div key={access.id} className="p-4 hover:bg-slate-50 transition-colors flex items-center justify-between group">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center text-xs font-bold shadow-sm">
                            {(access.grantedToName || access.grantedTo || '?').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-700">{access.grantedToName || t('docviewer.user', 'Usuario')}</p>
                            <p className="text-xs text-slate-400">{access.grantedTo || '...'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                            {access.permission === 'read' ? t('docviewer.read_only', 'Lectura') : t('docviewer.editor', 'Editor')}
                          </span>
                          <button
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                            onClick={() => handleRevokeAccess(access.id)}
                            title={t('docviewer.revoke_access', 'Revocar acceso')}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Preview */}
        <div className="lg:col-span-2">
          {/* Document Content Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden h-full min-h-[600px] flex flex-col">
            <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center text-white shadow-md shadow-slate-900/10">
                  <i className="fas fa-file-contract"></i>
                </div>
                <h3 className="font-bold text-slate-800">{t('preview.title', 'Vista Previa')}</h3>
              </div>
              {document.pdfHash && (
                <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 px-3 py-1.5 rounded-full border border-amber-100">
                  <i className="fas fa-lock"></i>
                  <span>{t('docviewer.view_only', 'Solo visualización')}</span>
                </div>
              )}
            </div>

            <div className="flex-1 bg-slate-100 p-1 relative flex flex-col">
              {document.pdfHash && document.ipfsHash && !document.ipfsHash.includes('Mock') ? (
                <div className="relative w-full h-full min-h-[600px] bg-white rounded-xl shadow-inner overflow-hidden border border-slate-200">
                  <iframe
                    src={`data:application/pdf;base64,${document.ipfsHash}`}
                    className="w-full h-full absolute inset-0"
                    title="Document Viewer"
                  />
                  {/* Watermark Overlay can go here if needed */}
                </div>
              ) : (
                <div className="flex-1 bg-slate-200/50 p-4 lg:p-8 flex justify-center items-center overflow-y-auto rounded-b-2xl">
                  {/* SVG Certificate based Template */}
                  <div className="w-full max-w-[210mm] aspect-[1.414/1] bg-white p-8 border-4 border-double border-slate-200 rounded-lg text-center relative overflow-hidden shadow-2xl flex flex-col items-center justify-center">

                    {/* Background SVG */}
                    <img
                      src="/templates/certificate-template.svg"
                      className="absolute inset-0 w-full h-full object-contain z-0"
                      alt=""
                    />

                    <div className="relative z-10 w-full px-12">
                      <div className="flex flex-col items-center mb-6">
                        <img src="/logo_transparente.png" className="h-16 mb-4 opacity-80" alt="Logo" />
                        <h1 className="text-3xl font-serif text-slate-900 tracking-widest uppercase">
                          {document.templateName || 'CERTIFICADO'}
                        </h1>
                        <div className="w-20 h-1 bg-blue-600 mt-2 rounded-full"></div>
                      </div>

                      <div className="mb-8">
                        <p className="text-sm text-slate-500 italic mb-2">{t('docviewer.certifies_that', 'Este documento certifica con honor que:')}</p>
                        <h2 className="text-4xl font-black text-slate-900 border-b-2 border-slate-200 inline-block px-10 pb-2 mb-2 tracking-tight">
                          {(document.fieldValues?.Nombre || t('docviewer.beneficiary', 'Beneficiario')).toString().toUpperCase()}
                        </h2>
                        <p className="text-xs text-slate-400 font-medium">{document.recipientEmail}</p>
                      </div>

                      <div className="max-w-xl mx-auto mb-10">
                        <p className="text-slate-600 text-base leading-relaxed">
                          {t('docviewer.fulfilled_requirements', 'Ha cumplido satisfactoriamente con todos los requisitos y estándares de calidad para la obtención del presente título,')}
                          {t('docviewer.registered_blockchain', 'debidamente registrado y verificado en la red de AereoCert Blockchain.')}
                        </p>
                      </div>

                      {/* Additional Metadata Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-6 max-w-2xl mx-auto text-left border-t border-slate-100 pt-8">
                        {Object.entries(document.fieldValues).filter(([k]) => k !== 'Nombre').map(([key, value]) => (
                          <div key={key}>
                            <p className="text-[7px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">{key}</p>
                            <p className="text-[11px] font-bold text-slate-800 border-l-2 border-blue-500 pl-2">{String(value)}</p>
                          </div>
                        ))}
                        <div>
                          <p className="text-[7px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">{t('cert_form.issue_date', 'Fecha Emisión')}</p>
                          <p className="text-[11px] font-bold text-slate-800 border-l-2 border-blue-500 pl-2">{formatDate(document.issuedAt)}</p>
                        </div>
                      </div>

                      {/* Footer with Signatures & QR */}
                      <div className="mt-12 flex justify-between items-end">
                        <div className="text-left">
                          <div className="mb-2">
                            <p className="text-[10px] font-script text-slate-800 italic">{t('docviewer.verified_signature', 'Firma Digital Verificada')}</p>
                            <div className="h-0.5 w-32 bg-slate-800"></div>
                          </div>
                          <p className="text-[9px] font-bold text-slate-900 uppercase">{document.issuerName}</p>
                          <p className="text-[7px] text-slate-400 uppercase">EMISOR AUTORIZADO • AEROCERT</p>
                        </div>

                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-[7px] font-bold text-slate-400">DOC ID</p>
                            <p className="text-[8px] font-mono text-slate-300">{document.id.substring(0, 16)}</p>
                            <p className="text-[7px] text-green-600 font-bold mt-1 tracking-widest">{t('docviewer.verified_online', 'VERIFICADO ONLINE')}</p>
                          </div>
                          <div className="bg-white p-1 rounded-lg border border-slate-200 shadow-lg">
                            <div className="w-16 h-16 bg-slate-900 flex items-center justify-center text-white text-[8px] font-bold text-center leading-tight">
                              VALIDACIÓN<br />QR
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Grant Access Modal */}
      {
        showAccessModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-scale-up" onClick={(e) => e.stopPropagation()}>
              <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <h3 className="font-bold text-lg text-slate-800">{t('docviewer.grant_access', 'Otorgar Acceso')}</h3>
                <button
                  className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-colors"
                  onClick={() => setShowAccessModal(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-slate-700">{t('docviewer.recipient_email', 'Email del destinatario')} *</label>
                  <div className="relative">
                    <input
                      type="email"
                      value={newAccessEmail}
                      onChange={(e) => setNewAccessEmail(e.target.value)}
                      placeholder="ejemplo@correo.com"
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                      required
                    />
                    <i className="fas fa-envelope absolute left-3.5 top-3.5 text-slate-400"></i>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-slate-700">{t('docviewer.name_optional', 'Nombre (opcional)')}</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={newAccessName}
                      onChange={(e) => setNewAccessName(e.target.value)}
                      placeholder={t('docviewer.full_name', 'Nombre completo')}
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                    />
                    <i className="fas fa-user absolute left-3.5 top-3.5 text-slate-400"></i>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-slate-700">{t('docviewer.permission', 'Permiso')}</label>
                    <select
                      value={newAccessPermission}
                      onChange={(e) => setNewAccessPermission(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all bg-white"
                    >
                      <option value="read">{t('docviewer.read_only', 'Solo lectura')}</option>
                      <option value="read_write">{t('docviewer.editor', 'Editor')}</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-slate-700">{t('docviewer.expiration', 'Vencimiento')}</label>
                    <input
                      type="datetime-local"
                      value={newAccessExpires}
                      onChange={(e) => setNewAccessExpires(e.target.value)}
                      className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all text-sm"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500 italic text-right">{t('docviewer.empty_permanent', 'Dejar vencimiento vacío para acceso permanente')}</p>
              </div>

              <div className="p-5 bg-slate-50 flex items-center justify-end gap-3 border-t border-slate-100">
                <button
                  className="px-4 py-2 rounded-lg text-slate-600 font-medium hover:bg-slate-200 transition-colors"
                  onClick={() => setShowAccessModal(false)}
                >
                  {t('common.cancel', 'Cancelar')}
                </button>
                <button
                  className="px-6 py-2 rounded-lg bg-blue-600 text-white font-bold shadow-lg shadow-blue-500/20 hover:bg-blue-700 active:scale-95 transition-all flex items-center gap-2"
                  onClick={handleGrantAccess}
                >
                  <i className="fas fa-check"></i>
                  <span>{t('common.confirm', 'Confirmar')}</span>
                </button>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};
